const BST_HEADER_SEARCH_COMPACT_MQ = '(min-width: 990px) and (max-width: 1199px)';

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this._predictiveSearchReady = false;
    this._compactMq = null;
    this._suppressFocusOut = false;
    this.onCompactMediaChange = this.onCompactMediaChange.bind(this);
    this.onSearchIconToggleClick = this.onSearchIconToggleClick.bind(this);
  }

  connectedCallback() {
    this.initSearchForm();
    this.initPredictiveSearch();
  }

  initPredictiveSearch() {
    if (this._predictiveSearchReady) return;

    this.predictiveSearchPanel = this.querySelector('.bst-header__predictive-results, .predictive-search--header');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.popularPanel = this.querySelector('[data-bst-popular-panel]');
    this.searchWrap = this.closest('.bst-header__search-wrap');
    this.searchField = this.querySelector('[data-bst-search-field]');
    this.searchBarHost = this.querySelector('[data-bst-search-bar-host]');
    this.popularSearchSlot = this.querySelector('[data-bst-popular-search-slot]');
    this.resultsSearchSlot = this.querySelector('[data-bst-search-results-slot]');
    this.searchIconToggle = this.querySelector('[data-bst-search-icon-toggle]');

    if (!this.input || !this.predictiveSearchResults) return;

    this._predictiveSearchReady = true;
    this.setupEventListeners();
    this.setupCompactSearch();
  }

  setupCompactSearch() {
    if (!this.searchWrap?.classList.contains('bst-header__search-wrap--desktop')) return;

    this._compactMq = window.matchMedia(BST_HEADER_SEARCH_COMPACT_MQ);
    this._compactMq.addEventListener('change', this.onCompactMediaChange);

    if (this.searchIconToggle) {
      this.searchIconToggle.addEventListener('click', this.onSearchIconToggleClick);
    }

    this.onCompactMediaChange(this._compactMq);
  }

  isCompactHeaderSearch() {
    return (
      Boolean(this.searchWrap?.classList.contains('bst-header__search-wrap--desktop')) &&
      window.matchMedia(BST_HEADER_SEARCH_COMPACT_MQ).matches
    );
  }

  onCompactMediaChange(event) {
    const mq = event.matches !== undefined ? event : this._compactMq;
    if (!mq?.matches) {
      this.restoreSearchFieldToBar();
      this.searchWrap?.classList.remove('is-search-open');
      if (this.searchIconToggle) {
        this.searchIconToggle.setAttribute('aria-expanded', 'false');
      }
      if (!this.isOpen) this.closeResults(false);
      return;
    }

    if (this.isOpen) {
      this.placeSearchFieldInPanel();
    }
  }

  onSearchIconToggleClick(event) {
    if (!this.isCompactHeaderSearch()) return;

    event.preventDefault();
    event.stopPropagation();

    if (this.isOpen || this.hasAttribute('open')) {
      this.close(true);
      return;
    }

    this.openCompactSearch();
  }

  openCompactSearch() {
    this._suppressFocusOut = true;

    if (!this.getQuery().length) {
      this.showPopularProducts();
    } else {
      if (this.isCompactHeaderSearch()) {
        this.placeSearchFieldForResults();
      }
      this.open();
    }

    window.setTimeout(() => {
      this.input?.focus({ preventScroll: true });
      window.setTimeout(() => {
        this._suppressFocusOut = false;
      }, 200);
    }, 0);
  }

  getActiveSearchFieldSlot() {
    if (!this.isCompactHeaderSearch()) return this.searchBarHost;

    const popularVisible = this.popularPanel && !this.popularPanel.hidden;
    if (popularVisible && this.popularSearchSlot) return this.popularSearchSlot;
    if (this.resultsSearchSlot) return this.resultsSearchSlot;
    return this.popularSearchSlot || this.searchBarHost;
  }

  placeSearchFieldInPanel() {
    if (!this.searchField) return;

    const slot = this.getActiveSearchFieldSlot();
    if (!slot || slot.contains(this.searchField)) return;

    slot.hidden = false;
    slot.appendChild(this.searchField);
    this.searchWrap?.classList.add('is-search-open');
    if (this.searchIconToggle) {
      this.searchIconToggle.setAttribute('aria-expanded', 'true');
    }
  }

  placeSearchFieldForPopular() {
    if (!this.isCompactHeaderSearch() || !this.popularSearchSlot) return;
    this.popularSearchSlot.hidden = false;
    this.popularSearchSlot.appendChild(this.searchField);
    this.searchWrap?.classList.add('is-search-open');
    if (this.searchIconToggle) {
      this.searchIconToggle.setAttribute('aria-expanded', 'true');
    }
  }

  placeSearchFieldForResults() {
    if (!this.isCompactHeaderSearch() || !this.resultsSearchSlot) return;
    this.resultsSearchSlot.hidden = false;
    this.resultsSearchSlot.appendChild(this.searchField);
    this.searchWrap?.classList.add('is-search-open');
    if (this.searchIconToggle) {
      this.searchIconToggle.setAttribute('aria-expanded', 'true');
    }
  }

  restoreSearchFieldToBar() {
    if (!this.searchField || !this.searchBarHost) return;
    if (this.searchBarHost.contains(this.searchField)) return;

    this.searchBarHost.appendChild(this.searchField);
    this.searchWrap?.classList.remove('is-search-open');

    if (this.resultsSearchSlot) this.resultsSearchSlot.hidden = true;
    if (this.searchIconToggle) {
      this.searchIconToggle.setAttribute('aria-expanded', 'false');
    }
  }

  setupEventListeners() {
    if (!this.input?.form) return;

    this.onFormSubmitHandler = this.onFormSubmitHandler || this.onFormSubmit.bind(this);
    this.onFocusHandler = this.onFocusHandler || this.onFocus.bind(this);
    this.onFocusOutHandler = this.onFocusOutHandler || this.onFocusOut.bind(this);
    this.onKeyupHandler = this.onKeyupHandler || this.onKeyup.bind(this);
    this.onKeydownHandler = this.onKeydownHandler || this.onKeydown.bind(this);

    this.input.form.addEventListener('submit', this.onFormSubmitHandler);
    this.input.addEventListener('focus', this.onFocusHandler);
    this.input.addEventListener('input', this.onSearchInputHandler);
    this.addEventListener('focusout', this.onFocusOutHandler);
    this.addEventListener('keyup', this.onKeyupHandler);
    this.addEventListener('keydown', this.onKeydownHandler);
  }

  onSearchInputHandler = () => {
    if (!this.getQuery().length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showPopularProducts();
    }
  };

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    if (!this.input) return;

    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.abortController.abort();
      this.abortController = new AbortController();
      this.showPopularProducts();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
      return;
    }

    const selectedLink = this.querySelector('[aria-selected="true"] a');
    if (selectedLink) {
      event.preventDefault();
      selectedLink.click();
    }
  }

  onFormReset(event) {
    if (!this.input) return;

    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.removeAttribute('results');
      this.removeAttribute('loading');
      if (this.predictiveSearchResults) {
        this.predictiveSearchResults.innerHTML = '';
      }
      this.showPopularProducts();
    }
  }

  onFocus() {
    if (!this.input) return;

    if (this.isCompactHeaderSearch()) {
      this.placeSearchFieldInPanel();
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      if (this.hasAttribute('open')) return;
      this.showPopularProducts();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this._suppressFocusOut) return;

      const active = document.activeElement;
      if (active && (this.contains(active) || this.searchWrap?.contains(active))) return;

      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.showPopularProducts();
    }

    switch (event.code) {
      case 'ArrowUp':
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption(event.code === 'ArrowUp' ? 'up' : 'down');
        break;
      case 'Enter':
        if (this.querySelector('[aria-selected="true"]')) {
          event.preventDefault();
          this.selectOption();
        }
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll(
        'li[role="option"], li.bst-predictive-search__grid-item[role="option"], li.predictive-search__list-item, button.predictive-search__item, button.bst-predictive-search__search-for'
      )
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.hidePopularProducts();
    this.setLiveRegionLoadingState();

    if (this.predictiveSearchPanel) this.predictiveSearchPanel.hidden = false;
    if (this.predictiveSearchResults && !this.cachedResults[queryKey]) {
      this.predictiveSearchResults.hidden = false;
      this.predictiveSearchResults.innerHTML = `<div class="predictive-search__loading-state" role="status"><span class="visually-hidden">${this.loadingText || 'Loading'}</span></div>`;
    }

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const currentQuery = this.getQuery();
        if (!currentQuery.length) {
          this.showPopularProducts();
          return;
        }

        if (currentQuery !== searchTerm) {
          return;
        }

        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');

        if (!section) {
          this.close();
          return;
        }

        const resultsMarkup = section.innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.textContent = statusText || '';
  }

  hasPopularProducts() {
    return Boolean(this.popularPanel?.querySelector('.bst-predictive-search__grid')?.children.length);
  }

  showPopularProducts() {
    if (!this.predictiveSearchPanel) return;

    this.searchTerm = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');

    const compact = this.isCompactHeaderSearch();
    const canShowPopular = this.hasPopularProducts() || compact;

    if (canShowPopular) {
      if (compact) {
        this.placeSearchFieldForPopular();
      }

      if (this.predictiveSearchPanel) {
        this.predictiveSearchPanel.hidden = false;
        this.predictiveSearchPanel.removeAttribute('hidden');
      }
      if (this.popularPanel) {
        this.popularPanel.hidden = false;
        this.popularPanel.removeAttribute('hidden');
      }
      if (this.predictiveSearchResults) this.predictiveSearchResults.hidden = true;

      this.open();
      return;
    }

    this.close();
  }

  hidePopularProducts() {
    if (this.popularPanel) this.popularPanel.hidden = true;
    if (this.predictiveSearchResults) this.predictiveSearchResults.hidden = false;

    if (this.isCompactHeaderSearch() && this.isOpen) {
      this.placeSearchFieldForResults();
    }
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.hidePopularProducts();
    if (this.predictiveSearchPanel) this.predictiveSearchPanel.hidden = false;
    this.predictiveSearchResults.hidden = false;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    try {
      this.setLiveRegionResults();
    } catch (error) {
      this.removeAttribute('loading');
    }

    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(liveRegion?.textContent?.trim() || '');
  }

  getResultsMaxHeight() {
    const anchor =
      this.closest('.bst-header__search-wrap') ||
      document.querySelector('.bst-header__bar') ||
      document.querySelector('.section-header') ||
      document.querySelector('.bst-header');
    const bottom = anchor?.getBoundingClientRect().bottom ?? 0;
    this.resultsMaxHeight = Math.max(window.innerHeight - bottom, 200);
    return this.resultsMaxHeight;
  }

  isStickyHeaderEnabled() {
    return Boolean(this.closest('.bst-header--sticky-enabled'));
  }

  updateStickySearchPosition() {
    const header = this.closest('.bst-header');
    if (!header?.classList.contains('bst-header--sticky-enabled')) return;
    const bar = header.querySelector('.bst-header__bar');
    if (!bar) return;
    const bottom = Math.ceil(bar.getBoundingClientRect().bottom);
    header.style.setProperty('--bst-search-panel-top', `${bottom}px`);
  }

  closeBstMegaMenus() {
    const header = this.closest('.bst-header');
    header?._bstHeaderInit?.closeAllMenus?.();
  }

  bindStickySearchPosition() {
    if (!this.isStickyHeaderEnabled()) return;
    this._stickySearchPositionHandler =
      this._stickySearchPositionHandler ||
      (() => {
        if (this.isOpen) this.updateStickySearchPosition();
      });
    window.addEventListener('scroll', this._stickySearchPositionHandler, { passive: true });
    window.addEventListener('resize', this._stickySearchPositionHandler, { passive: true });
  }

  unbindStickySearchPosition() {
    if (!this._stickySearchPositionHandler) return;
    window.removeEventListener('scroll', this._stickySearchPositionHandler);
    window.removeEventListener('resize', this._stickySearchPositionHandler);
  }

  setHeaderSearchOpenState(isOpen) {
    const header = this.closest('.bst-header');
    if (!header) return;
    header.classList.toggle('bst-header--search-open', isOpen);
    if (!isOpen) header.style.removeProperty('--bst-search-panel-top');
  }

  open() {
    const panel = this.predictiveSearchPanel || this.predictiveSearchResults;
    if (!panel) return;

    if (this.isCompactHeaderSearch()) {
      this.placeSearchFieldInPanel();
    }

    if (this.isStickyHeaderEnabled()) {
      this.closeBstMegaMenus();
      this.updateStickySearchPosition();
      this.bindStickySearchPosition();
      this.setHeaderSearchOpenState(true);
    }

    if (this.predictiveSearchPanel) {
      this.predictiveSearchPanel.hidden = false;
      this.predictiveSearchPanel.removeAttribute('hidden');
    }

    const maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
    if (Number.isFinite(maxHeight)) {
      panel.style.maxHeight = `${maxHeight}px`;
    }

    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (!this.input) return;

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    this.hidePopularProducts();

    if (this.popularPanel) this.popularPanel.hidden = true;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = '';
      this.predictiveSearchResults.hidden = true;
    }
    if (this.predictiveSearchPanel) this.predictiveSearchPanel.hidden = true;

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = false;

    const panel = this.predictiveSearchPanel || this.predictiveSearchResults;
    panel?.removeAttribute('style');

    this.unbindStickySearchPosition();
    this.setHeaderSearchOpenState(false);

    if (this.isCompactHeaderSearch()) {
      this.restoreSearchFieldToBar();
    }
  }

  disconnectedCallback() {
    this._compactMq?.removeEventListener('change', this.onCompactMediaChange);
    this.unbindStickySearchPosition();
    this.setHeaderSearchOpenState(false);
  }
}

customElements.define('predictive-search', PredictiveSearch);
